<template>
  <div class="cloud-drive-files">
    <div class="header">
      <button @click="$router.back()" class="btn-back">← 返回</button>
      <h1>{{ spaceInfo.share_space_name || '共享云盘' }}</h1>
    </div>

    <div class="breadcrumb" v-if="breadcrumb.length > 0">
      <span
        v-for="(item, index) in breadcrumb"
        :key="item.id"
        @click="goToFolder(item.id)"
        class="breadcrumb-item"
      >
        {{ item.name }}
        <span v-if="index < breadcrumb.length - 1"> / </span>
      </span>
    </div>

    <div class="content">
      <div v-if="loading" class="loading">加载中...</div>

      <div v-else-if="files.length === 0" class="empty">
        <p>暂无文件</p>
      </div>

      <div v-else class="file-list">
        <div
          v-for="file in files"
          :key="file.id"
          class="file-item"
          @click="handleFileClick(file)"
        >
          <div class="file-icon">
            {{ file.is_dir ? '📁' : '📄' }}
          </div>
          <div class="file-info">
            <div class="file-name">{{ file.name }}</div>
            <div class="file-meta">
              <span v-if="!file.is_dir">{{ formatSize(file.size) }}</span>
              <span>{{ formatDate(file.updated_at) }}</span>
            </div>
          </div>
          <div class="file-actions" v-if="!file.is_dir">
            <button @click.stop="handleDownload(file)" class="btn-action">下载</button>
            <button @click.stop="handlePreview(file)" class="btn-action">预览</button>
          </div>
        </div>
      </div>

      <div v-if="pagination.total > pagination.per_page" class="pagination">
        <button
          @click="loadPage(pagination.current_page - 1)"
          :disabled="pagination.current_page === 1"
        >
          上一页
        </button>
        <span>{{ pagination.current_page }} / {{ pagination.last_page }}</span>
        <button
          @click="loadPage(pagination.current_page + 1)"
          :disabled="pagination.current_page === pagination.last_page"
        >
          下一页
        </button>
      </div>
    </div>

    <!-- Token 失效弹窗 -->
    <AuthModal :visible="showAuthModal" @relogin="handleRelogin" />
  </div>
</template>

<script>
import { fetchShareFiles, fetchFileDownloadUrl, fetchFilePreviewUrl } from '@/api/cloudDrive'
import AuthModal from '@/components/AuthModal.vue'
import authErrorHandler from '@/mixins/authErrorHandler'
import { isPptFile, openPptPreviewInNewPage } from '@/utils/pptPreview'

export default {
  name: 'CloudDriveFiles',
  components: {
    AuthModal
  },
  mixins: [authErrorHandler],
  data() {
    return {
      loading: true,
      files: [],
      spaceInfo: {},
      currentParentId: 0,
      breadcrumb: [],
      pagination: {
        current_page: 1,
        total: 0,
        per_page: 20,
        last_page: 1
      }
    }
  },
  computed: {
    spaceId() {
      return Number(this.$route.params.spaceId)
    }
  },
  async mounted() {
    await this.loadFiles()
  },
  methods: {
    async loadFiles(parentId = 0, page = 1) {
      try {
        this.loading = true
        const result = await fetchShareFiles(this.spaceId, {
          parent_id: parentId,
          page,
          pageSize: 20
        })

        this.files = result.data
        this.spaceInfo = result.space || {}
        this.currentParentId = parentId
        this.pagination = {
          current_page: result.current_page,
          total: result.total,
          per_page: result.per_page,
          last_page: result.last_page
        }
      } catch (err) {
        if (this.isAuthError(err)) {
          this.handleAuthError()
        } else {
          alert(err.message || '加载失败')
        }
      } finally {
        this.loading = false
      }
    },

    loadPage(page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.loadFiles(this.currentParentId, page)
      }
    },

    handleFileClick(file) {
      if (file.is_dir) {
        this.breadcrumb.push({ id: file.id, name: file.name })
        this.loadFiles(file.id)
      }
    },

    goToFolder(folderId) {
      const index = this.breadcrumb.findIndex(item => item.id === folderId)
      if (index !== -1) {
        this.breadcrumb = this.breadcrumb.slice(0, index + 1)
      }
      this.loadFiles(folderId)
    },

    async handleDownload(file) {
      try {
        const result = await fetchFileDownloadUrl(this.spaceId, file.id)
        if (result.url) {
          window.open(result.url, '_blank')
        }
      } catch (err) {
        if (this.isAuthError(err)) {
          this.handleAuthError()
        } else {
          alert(err.message || '获取下载地址失败')
        }
      }
    },

    async handlePreview(file) {
      try {
        if (isPptFile(file)) {
          openPptPreviewInNewPage(this.$router, {
            file,
            fileId: file.id,
            shareSpaceId: this.spaceId,
            title: file.name
          })
          return
        }

        const result = await fetchFilePreviewUrl(this.spaceId, file.id)
        if (result.url) {
          window.open(result.url, '_blank')
        }
      } catch (err) {
        if (this.isAuthError(err)) {
          this.handleAuthError()
        } else {
          alert(err.message || '获取预览地址失败')
        }
      }
    },

    formatSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return dateStr.split(' ')[0]
    }
  }
}
</script>

<style scoped>
.cloud-drive-files {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.btn-back {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  background: white;
  border-radius: 4px;
  cursor: pointer;
}

.btn-back:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.header h1 {
  font-size: 24px;
  color: #333;
  margin: 0;
}

.breadcrumb {
  margin-bottom: 20px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.breadcrumb-item {
  cursor: pointer;
  color: #1890ff;
}

.breadcrumb-item:hover {
  text-decoration: underline;
}

.loading,
.empty {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.file-list {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.2s;
}

.file-item:hover {
  background: #fafafa;
}

.file-item:last-child {
  border-bottom: none;
}

.file-icon {
  font-size: 32px;
  margin-right: 16px;
}

.file-info {
  flex: 1;
}

.file-name {
  font-size: 16px;
  color: #333;
  margin-bottom: 4px;
}

.file-meta {
  font-size: 12px;
  color: #999;
}

.file-meta span {
  margin-right: 16px;
}

.file-actions {
  display: flex;
  gap: 8px;
}

.btn-action {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-action:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 30px;
}

.pagination button {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  background: white;
  border-radius: 4px;
  cursor: pointer;
}

.pagination button:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

.pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
